import type { BlockContent, BlockGroup, BlockType } from '@/lib/dashboard'
import type { PersonaSlug } from '@/lib/personas'
import { audioEmbed, videoEmbed } from '@/components/profile/embed'
import { socialBrandFromUrl, socialName } from '@/components/dashboard/socials'

/**
 * What sign-up shows each persona: the page forming in the preview, and the
 * first blocks it suggests. Kept apart from lib/personas.ts, which describes
 * the landing pages, because these are sized and placed, and the landing
 * pages only name them.
 */

/** A block as the preview lays it out: its drawing, and how much of the two-wide grid it takes. */
export interface PreviewTile {
  type: BlockType
  group: BlockGroup
  w: 1 | 2
  h: 1 | 2
}

/**
 * The page each persona sees forming beside the form, filled top to bottom in
 * a two-wide grid. Mostly free blocks: these are a promise about what the page
 * can be on the plan they are signing up to. The paid ones appear only where
 * selling is the whole point (a shop, an artist's merch).
 */
export const PREVIEW_TILES: Record<PersonaSlug, PreviewTile[]> = {
  person: [
    { type: 'audio', group: 'media', w: 2, h: 1 },
    { type: 'image', group: 'media', w: 1, h: 2 },
    { type: 'social', group: 'basic', w: 1, h: 1 },
    { type: 'text', group: 'basic', w: 1, h: 1 },
    { type: 'pet', group: 'interactive', w: 2, h: 1 },
  ],
  creator: [
    { type: 'video', group: 'media', w: 2, h: 2 },
    { type: 'social', group: 'basic', w: 1, h: 1 },
    { type: 'link', group: 'basic', w: 1, h: 1 },
    { type: 'qa', group: 'interactive', w: 2, h: 1 },
  ],
  artist: [
    { type: 'audio', group: 'media', w: 2, h: 1 },
    { type: 'video', group: 'media', w: 1, h: 2 },
    { type: 'countdown', group: 'interactive', w: 1, h: 1 },
    { type: 'product', group: 'commerce', w: 1, h: 1 },
    { type: 'social', group: 'basic', w: 2, h: 1 },
  ],
  organizer: [
    { type: 'ticket', group: 'commerce', w: 2, h: 1 },
    { type: 'image', group: 'media', w: 1, h: 2 },
    { type: 'countdown', group: 'interactive', w: 1, h: 1 },
    { type: 'map', group: 'support', w: 1, h: 1 },
    { type: 'chat', group: 'support', w: 2, h: 1 },
  ],
  business: [
    { type: 'booking', group: 'commerce', w: 2, h: 1 },
    { type: 'product', group: 'commerce', w: 1, h: 1 },
    { type: 'hours', group: 'support', w: 1, h: 1 },
    { type: 'map', group: 'support', w: 2, h: 1 },
    { type: 'chat', group: 'support', w: 2, h: 1 },
  ],
}

/** The drawing each persona's row in the picker is shown with. */
export const PERSONA_SIGNATURE: Record<PersonaSlug, PreviewTile> = {
  person: PREVIEW_TILES.person[0],
  creator: PREVIEW_TILES.creator[0],
  artist: PREVIEW_TILES.artist[2],
  organizer: PREVIEW_TILES.organizer[0],
  business: PREVIEW_TILES.business[0],
}

/** Which persona asks for a phone at sign-up: the ones that take money. */
export function personaNeedsPhone(persona: PersonaSlug | null): boolean {
  return persona === 'business' || persona === 'organizer'
}

/**
 * A first block worth suggesting.
 *
 * `paste` ideas are filled by pasting a link, which is then read for what it
 * is; `hint` is the kind of link the field should invite. `text` and `image`
 * are typed or picked right there. Only free types, so nothing suggested here
 * is refused by the plan they are on.
 */
export interface FirstIdea {
  id: 'song' | 'photo' | 'thought' | 'video' | 'socials' | 'track' | 'clip' | 'poster' | 'nextEvent' | 'tickets' | 'menu' | 'product' | 'about' | 'aboutMe'
  kind: 'paste' | 'text' | 'image'
  tile: PreviewTile
  hint?: string
}

const AUDIO: PreviewTile = { type: 'audio', group: 'media', w: 2, h: 1 }
const VIDEO: PreviewTile = { type: 'video', group: 'media', w: 2, h: 2 }
const IMAGE: PreviewTile = { type: 'image', group: 'media', w: 2, h: 2 }
const TEXT: PreviewTile = { type: 'text', group: 'basic', w: 2, h: 1 }
const SOCIAL: PreviewTile = { type: 'social', group: 'basic', w: 1, h: 1 }
const LINK: PreviewTile = { type: 'link', group: 'basic', w: 2, h: 1 }

export const FIRST_IDEAS: Record<PersonaSlug, FirstIdea[]> = {
  person: [
    { id: 'song', kind: 'paste', tile: AUDIO, hint: 'https://open.spotify.com/track/...' },
    { id: 'photo', kind: 'image', tile: IMAGE },
    { id: 'thought', kind: 'text', tile: TEXT },
  ],
  creator: [
    { id: 'video', kind: 'paste', tile: VIDEO, hint: 'https://www.tiktok.com/@.../video/...' },
    { id: 'socials', kind: 'paste', tile: SOCIAL, hint: 'https://instagram.com/...' },
    { id: 'aboutMe', kind: 'text', tile: TEXT },
  ],
  artist: [
    { id: 'track', kind: 'paste', tile: AUDIO, hint: 'https://open.spotify.com/track/...' },
    { id: 'clip', kind: 'paste', tile: VIDEO, hint: 'https://youtube.com/watch?v=...' },
    { id: 'photo', kind: 'image', tile: IMAGE },
  ],
  organizer: [
    { id: 'poster', kind: 'image', tile: IMAGE },
    { id: 'nextEvent', kind: 'text', tile: TEXT },
    { id: 'tickets', kind: 'paste', tile: LINK, hint: 'https://...' },
  ],
  business: [
    { id: 'menu', kind: 'paste', tile: LINK, hint: 'https://...' },
    { id: 'product', kind: 'image', tile: IMAGE },
    { id: 'about', kind: 'text', tile: TEXT },
  ],
}

/** What a pasted link becomes. `label` is `video`, `audio`, `link`, or a social brand's name. */
export interface Pasted {
  type: 'video' | 'audio' | 'social' | 'link'
  content: BlockContent
  tile: PreviewTile
  brand?: string
}

/**
 * Reads a pasted link for the block it should be: a playable video, a
 * playable track, a profile on a known network, or failing all of those a
 * plain link. Null when it is not a link at all.
 *
 * In that order on purpose. A YouTube watch link is a video before it is a
 * YouTube profile, and a TikTok video before it is a TikTok account.
 */
export function readPasted(typed: string): Pasted | null {
  const text = typed.trim()
  if(!text || /\s/.test(text)) return null
  const url = /^https?:\/\//i.test(text) ? text : `https://${text}`
  let parsed: URL
  try { parsed = new URL(url) } catch { return null }
  // A host with a dot, so "hello" is not read as https://hello.
  if(!parsed.hostname.includes('.')) return null

  if(videoEmbed(url, '')) return { type: 'video', content: { url }, tile: VIDEO }
  if(audioEmbed(url, '')) return { type: 'audio', content: { url }, tile: AUDIO }
  const brand = socialBrandFromUrl(url)
  if(brand) return { type: 'social', content: { brand, url }, tile: SOCIAL, brand: socialName(brand) }
  return { type: 'link', content: { url, title: parsed.hostname.replace(/^www\./, '') }, tile: LINK }
}

/** Plain text as the rich text a text block stores: one paragraph per line, escaped. */
export function textToRich(value: string): string {
  const escape = (line: string) => line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return value.trim().split(/\n+/).map(line => `<p>${escape(line.trim())}</p>`).join('')
}
